import {
  BankQueueCounts,
  DocumentQueueCounts,
  FiledComplianceEntity,
  FilingDeadlineEntity,
  FilingsCalendarEntity,
  HomeSummaryEntity,
  JournalQueueCounts,
  QueryQueueCounts,
} from "../../domain/entities/HomeSummaryEntity";
import {
  BankQueueCountsDTO,
  DocumentQueueCountsDTO,
  FiledComplianceDTO,
  FilingDeadlineDTO,
  HomeSummaryDTO,
  JournalQueueCountsDTO,
  QueryQueueCountsDTO,
} from "../models/HomeSummaryDTO";

/** Coerces anything the API might send into a non-negative integer. */
const toCount = (value: unknown): number => {
  const parsed = Number(value);

  return Number.isFinite(parsed) && parsed > 0 ? Math.trunc(parsed) : 0;
};

const toText = (value: unknown): string =>
  typeof value === "string" ? value : "";

/**
 * Translates the `GET /api/v1/home/summary` payload into domain entities.
 *
 * Every field is defensively read. A partially-populated tenant (no company
 * profile, no compliance calendar) must render an empty page, never a crash.
 */
export class HomeSummaryMapper {
  static toDomain(dto: HomeSummaryDTO): HomeSummaryEntity {
    const groups = dto?.groups;
    const companyId = dto?.company?.id;

    return {
      user: {
        id: toCount(dto?.user?.id),
        name: toText(dto?.user?.name),
      },
      company: {
        id: companyId === null || companyId === undefined ? null : toCount(companyId),
        name: toText(dto?.company?.name),
      },
      asOf: toText(dto?.as_of),
      totalOpenItems: toCount(dto?.total_open_items),
      groups: {
        invoices: this.toDocumentCounts(groups?.invoices),
        bills: this.toDocumentCounts(groups?.bills),
        bank: this.toBankCounts(groups?.bank),
        queries: this.toQueryCounts(groups?.queries),
        journals: this.toJournalCounts(groups?.journals),
      },
      filings: this.toFilings(dto?.filings),
    };
  }

  private static toDocumentCounts(dto?: DocumentQueueCountsDTO): DocumentQueueCounts {
    return {
      onHold: toCount(dto?.on_hold),
      underReview: toCount(dto?.under_review),
      toReview: toCount(dto?.to_review),
    };
  }

  private static toBankCounts(dto?: BankQueueCountsDTO): BankQueueCounts {
    return {
      toCategorize: toCount(dto?.to_categorize),
      onHold: toCount(dto?.on_hold),
      flagged: toCount(dto?.flagged),
    };
  }

  private static toQueryCounts(dto?: QueryQueueCountsDTO): QueryQueueCounts {
    return {
      openAssignedToMe: toCount(dto?.open_assigned_to_me),
      pendingClientReplies: toCount(dto?.pending_client_replies),
      pendingSeniorReplies: toCount(dto?.pending_senior_replies),
    };
  }

  private static toJournalCounts(dto?: JournalQueueCountsDTO): JournalQueueCounts {
    return {
      pendingMyApproval: toCount(dto?.pending_my_approval),
      activeRecurringTemplates: toCount(dto?.active_recurring_templates),
    };
  }

  private static toFilings(dto?: HomeSummaryDTO["filings"]): FilingsCalendarEntity {
    return {
      horizonDays: toCount(dto?.horizon_days),
      overdue: (dto?.overdue ?? []).map((row) => this.toDeadline(row)),
      upcoming: (dto?.upcoming ?? []).map((row) => this.toDeadline(row)),
      recentlyFiled: (dto?.recently_filed ?? []).map((row) => this.toFiled(row)),
    };
  }

  private static toDeadline(dto: FilingDeadlineDTO): FilingDeadlineEntity {
    const days = Number(dto?.days_until_due);

    return {
      id: toCount(dto?.id),
      formTitle: toText(dto?.form_title),
      periodLabel: toText(dto?.period_label),
      dueDate: toText(dto?.due_date),
      dueDateLabel: toText(dto?.due_date_label),
      // Unlike the counts, a negative value here is meaningful (overdue), so this
      // deliberately does not go through toCount().
      daysUntilDue: Number.isFinite(days) ? Math.trunc(days) : null,
      isOverdue: Boolean(dto?.is_overdue),
      status: toText(dto?.status),
    };
  }

  private static toFiled(dto: FiledComplianceDTO): FiledComplianceEntity {
    return {
      id: toCount(dto?.id),
      formTitle: toText(dto?.form_title),
      periodLabel: toText(dto?.period_label),
      filedOn: toText(dto?.filed_on),
      filedOnLabel: toText(dto?.filed_on_label),
      status: toText(dto?.status),
    };
  }
}
